import { useCallback, useMemo } from "react";
import { usePageData } from "@insightfulscience/smart-react/hocs/DiPageHoC";
import { isFunc } from "@insightfulscience/shared-utils/fn";
import { validateForm } from "./form";
import { PURCHASE_TYPE } from "../domain/purchase";

const getSubscriptionSize = ({ subscriptionSize }) => subscriptionSize;
const getNewSeats = ({ newSeats }) => newSeats;

const getSeatsMap = new Map([
	[PURCHASE_TYPE.SUBSCRIPTION, getSubscriptionSize],
	[PURCHASE_TYPE.RENEW, getSubscriptionSize],
	[PURCHASE_TYPE.ADDON, getNewSeats],
]);

function useSeats(purchaseType = PURCHASE_TYPE.SUBSCRIPTION, initialSeats, filterPrices) {
	const { prices: allPrices, subscription } = usePageData();

	const prices = useMemo(
		() =>
			isFunc(filterPrices)
				? allPrices.filter(price => filterPrices(price, subscription))
				: allPrices,
		[allPrices], //
	);

	const getSeats = useMemo(
		() => getSeatsMap.get(purchaseType),
		[purchaseType], //
	);

	if (getSeats == null) throw new Error(`Unknown Purchase Type "${purchaseType}"`);

	const priceItemBySeats = useCallback(
		seats =>
			prices.find(priceItem => getSeats(priceItem) === seats) || {
				price: null,
				renewalPrice: null,
			},
		[prices],
	);

	const seatsList = useMemo(
		() => prices.map(getSeats),
		[prices], // deps
	);

	const minSeats = useMemo(() => Math.min(...seatsList), [seatsList]);

	const maxSeats = useMemo(() => Math.max(...seatsList), [seatsList]);

	const initialFormState = useMemo(
		() => {
			const seats = initialSeats ?? getSeats(prices[0]);
			return {
				...priceItemBySeats(seats),
				seats,
				minSeats,
				maxSeats,
				subscriptionID: subscription?.id,
			};
		},
		[prices], // deps
	);

	const updatePrice = useCallback(
		(state, seats) => ({
			...state,
			...priceItemBySeats(seats),
			seats,
		}),
		[prices],
	);

	return useMemo(
		() => ({
			updatePrice,
			minSeats,
			maxSeats,
			initialState: initialFormState,
			validateForm,
		}),
		[updatePrice, minSeats, maxSeats, initialFormState],
	);
}

export default useSeats;
